import { z } from "zod";

/** Every kind of reference content Hexling stores. One table, many categories. */
export const CONTENT_CATEGORIES = [
  "plant",
  "crystal",
  "oil",
  "recipe",
  "tarot_card",
  "sabbat",
  "zodiac_sign",
  "planet",
  "house",
  "aspect",
  "moon_phase",
  "element",
  "color",
  "day_of_week",
  "chakra",
  "intent",
  "familiar_type",
  "course",
  "lesson",
] as const;
export const contentCategory = z.enum(CONTENT_CATEGORIES);
export type ContentCategory = z.infer<typeof contentCategory>;

export const slug = z
  .string()
  .min(1)
  .max(120)
  .regex(
    /^[a-z0-9]+(?:[-_][a-z0-9]+)*(?:--[a-z0-9-]+)?$/,
    "Use lowercase letters, numbers and dashes",
  );

const petStatus = z.enum(["toxic", "non_toxic", "not_listed"]);
const ingestion = z.enum(["culinary", "caution", "do_not_ingest"]);
const strings = z.array(z.string().min(1));

export const plantData = z.object({
  latin_name: z.string(),
  other_names: strings,
  element: z.string(),
  planet: z.string(),
  intents: strings,
  description: z.string().min(1),
  safety: z.object({
    ingestion,
    human_cautions: strings,
    pregnancy_caution: z.boolean(),
    pets: z.object({
      cats: petStatus,
      dogs: petStatus,
      horses: petStatus,
      source: z.string(),
      verified_cats_against_aspca: z.boolean(),
      pet_friendly: z.boolean(),
    }),
  }),
});

export const crystalData = z.object({
  chemical_composition: z.string(),
  mohs_hardness: z.number().min(0).max(10).nullable(),
  colors: strings,
  element: z.string(),
  planet: z.string(),
  chakras: strings,
  zodiac: strings,
  intents: strings,
  description: z.string().min(1),
  care: z.object({
    water_safe: z.boolean(),
    sun_safe: z.boolean(),
    salt_safe: z.boolean(),
    toxic_handling: z.boolean(),
    recommended_cleansing: strings,
    elixir_method: z.enum(["never", "indirect_only", "indirect_preferred"]),
  }),
});

export const oilData = z.object({
  oil_type: z.enum(["essential", "carrier"]),
  latin_name: z.string().optional(),
  source_plant_slug: z.string().nullable().optional(),
  element: z.string().optional(),
  planet: z.string().optional(),
  intents: strings.optional(),
  description: z.string().min(1),
  safety: z.object({
    ingestion,
    human_cautions: strings,
    phototoxic: z.boolean().optional(),
    skin_sensitizer: z.boolean().optional(),
    pregnancy_caution: z.boolean().optional(),
    max_topical_dilution_pct: z.number().min(0).max(100).optional(),
    pets: z
      .object({
        cats: z.enum(["avoid", "caution"]),
        dogs: z.enum(["avoid", "caution"]),
        birds: z.enum(["avoid", "caution"]),
        note: z.string(),
      })
      .optional(),
  }),
});

export const recipeIngredient = z.object({
  kind: z.enum(["plant", "oil", "crystal", "other"]),
  ref_slug: z.string().nullable(),
  name: z.string().min(1),
  amount: z.number().positive(),
  unit: z.string().min(1),
  note: z.string().nullable(),
});

export const recipeData = z.object({
  type: z.string().min(1),
  use: z.enum([
    "burn",
    "drink",
    "eat",
    "bathe",
    "anoint",
    "carry",
    "keep",
    "bury_or_keep",
    "sprinkle",
    "wash",
    "simmer",
    "various",
  ]),
  intents: strings,
  ingredients: z.array(recipeIngredient).min(1),
  steps: z.array(z.string().min(1)).min(1),
  notes: z.string().nullable(),
  /** Derived by the API from ingredients; any client-sent value is replaced. */
  safety: z.unknown().optional(),
});

export const tarotData = z
  .object({
    arcana: z.enum(["major", "minor"]),
    suit: z.string().nullable(),
    number_numerology: z.number().int(),
    element: z.string(),
    keywords_upright: strings,
    keywords_reversed: strings,
    meaning_upright: z.string(),
    meaning_reversed: z.string(),
  })
  .loose();

export const lessonData = z.object({
  course_slug: slug,
  summary: z.string(),
  objectives: strings,
  estimated_minutes: z.number().int().positive(),
  status: z.enum(["draft_outline", "draft", "published"]),
  body_markdown: z.string().nullable(),
});

export const courseData = z.object({
  level: z.enum(["beginner", "intermediate", "advanced"]),
  icon: z.string(),
  description: z.string(),
});

/** Categories without a strict schema accept any JSON object. */
const looseData = z.record(z.string(), z.unknown());

export const CATEGORY_DATA_SCHEMAS: Record<ContentCategory, z.ZodType> = {
  plant: plantData,
  crystal: crystalData,
  oil: oilData,
  recipe: recipeData,
  tarot_card: tarotData,
  lesson: lessonData,
  course: courseData,
  sabbat: looseData,
  zodiac_sign: looseData,
  planet: looseData,
  house: looseData,
  aspect: looseData,
  moon_phase: looseData,
  element: looseData,
  color: looseData,
  day_of_week: looseData,
  chakra: looseData,
  intent: looseData,
  familiar_type: looseData,
};

/** An admin create/update of a content item. `data` is validated against its category. */
export const contentItemInput = z
  .object({
    category: contentCategory,
    slug,
    name: z.string().min(1).max(200),
    summary: z.string().max(500).default(""),
    sort_order: z.number().int().default(0),
    data: z.record(z.string(), z.unknown()),
  })
  .superRefine((value, ctx) => {
    const result = CATEGORY_DATA_SCHEMAS[value.category].safeParse(value.data);
    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue({
          code: "custom",
          path: ["data", ...issue.path.map(String)],
          message: issue.message,
        });
      }
    }
  });
export type ContentItemInput = z.infer<typeof contentItemInput>;

/** A content item as served by the API. */
export interface ContentItem {
  id: string;
  category: ContentCategory;
  slug: string;
  name: string;
  summary: string;
  sort_order: number;
  data: Record<string, unknown>;
  updated_at: string;
  deleted_at: string | null;
}

export const searchQuery = z.object({
  q: z.string().trim().min(1).max(100),
  category: contentCategory.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});

export const contentListQuery = z.object({
  category: contentCategory,
});

/** Content bundle delta: everything changed since `since` (ISO), or everything. */
export const contentBundleQuery = z.object({
  since: z.iso.datetime({ offset: true }).optional(),
});
